import React, { useCallback, useMemo } from 'react';
import classnames from 'classnames';
import uniq from 'lodash/uniq';
import isNil from 'lodash/isNil';
import styled from '@emotion/styled';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import Button from '@atlaskit/button';
import Tooltip from '@atlaskit/tooltip';
import InfoIcon from '@atlaskit/icon/glyph/info';
import { IconButton } from '@atlaskit/button/new';
import { Checkbox } from '@atlaskit/checkbox';
import { SkeletonItem } from '@atlaskit/menu';
import { PricingPlanType } from '@atlassian/cssxp-typescommon';

import { Flex, Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { ExpandableRow, FormattedDate } from '@css-xp/base';
import { isStatuspage } from '@atlassian/cssxp-utils-product';
import {
  splitAndCapitalize,
  ProductRelationShipType,
  getProductsSearchableRelationshipTypes,
  isStatusPageAddOnProduct,
} from '@css-xp/ppc-utils';
import {
  ROVO_PRODUCT_ID,
  STATSIG_FEATURE_GATES,
  STATUSPAGE_ADDON_PRODUCT_ID,
  STATUSPAGE_PRODUCT_ID,
} from '@css-xp/ppc-constants';
import { ActiveUsersCell, EntitlementItemCell, ProductStatusLozenge } from '@css-xp/ppc-purchase-experience';
import { NoData, FormatCurrency } from '@css-xp/ppc-common';
import { AddNewProductsWrapper } from '../../../add-new-products';
import { CartProductSchema, LinkedEntitlementRelationShipType, OperationType } from '../../../../types';
import { Product, ProductIdPlanOptionsMap, cartLinkedEntitlement } from '../types';
import { ViewProductPromotions } from './view-product-promotions';
import {
  usePricingPlanEligibility,
  useStore,
  useDestinationBillingProfileDetails,
  useOrgProductInstancesMap,
} from '../../../../store';
import { CART_COLUMNS_CONSTANTS } from './product-line-items-table';
import { EnrichmentStatus, State } from '../../../../store/types';
import { SelectType } from '../../../billing-profile/src/types';
import { getTrialStatusForEntitlement } from '../../../../store/utils/get-trial-status-for-entitlement';
import { CatalogueContextItems } from '../../../add-new-products/src/types';
import EditIcon from '@atlaskit/icon/core/edit';
import { LineItemPlanCell } from './line-item-plan-cell';

type CartLineItemProps = {
  columns: string[];
  product: cartLinkedEntitlement | Product;
  linkedEntitlements?: cartLinkedEntitlement[];
  isApp: boolean;
  productIdPlanOptionsMap: ProductIdPlanOptionsMap;
  selectedBillDate?: string;
  setSelectedPricingPlanId: (
    updatedPricingPlanId: string,
    updatedOfferingId: string,
    productId: CartProductSchema['productId']
  ) => void;
  setActiveUsers: (plan: string, productId: CartProductSchema['productId']) => void;
  onRemoveProductFromCart: (productId: CartProductSchema['productId']) => void;
  handleCheckedProductLineItems: (productId: string) => void;
  selectiveProductRemovalEnable: boolean;
  showTotalColumn?: boolean;
  showTaxEstimationColumn?: boolean;
  showProductsPromotionView?: boolean;
  shouldShowTrialStatus?: boolean;
  matchBillDateEnabled?: boolean;
  checkedBillDate?: Record<string, string>;
  enrichmentStatus: State['enrichmentStatus'];
  isAllowedToEditUsersCount?: boolean;
  isRowHighlighedForReview?: boolean;
  hideCustomerContactColumn?: boolean;
  isOrgProduct?: boolean;
  showSandboxProducts?: boolean;
  isSandboxDecouplingEnabled?: boolean;
  isNonCommercialPricingEnabled?: boolean;
  isExpandableRow?: boolean;
  cloudSiteId?: string;
  cloudSite?: string;
  handleMatchBillDates?: (lineItemId?: string) => void;
  checkedItemMap: Record<string, SelectType>;
  isTWCPhase2Enabled?: boolean;
  handleAddNewAppsClick?: (params: {
    product: any;
    cloudSiteId: string;
    cloudSiteUrl: string;
    onAddToCart: (OrderItems: CatalogueContextItems) => void;
    sandboxCloudSiteParentProductIds: string[];
  }) => void;
  isDefaultExpanded?: boolean;
  isAppificationEnabled?: boolean;
  setHasErrorFetchingOfferings?: (hasError: boolean) => void;
  isMultiCurrencyEnabled?: boolean;
  isAddOnsSearchEnabled?: boolean;
};

const SpacingBeforeIntegratedApps = styled.div({
  padding: '12px',
});

const StyledLoaderWrapper = styled.span({
  '> div': {
    paddingLeft: 0,
  },
});

const NextBillDateCellStyles = styled.div`
  display: flex !important;
  align-items: center;
  justify-content: flex-start;
  cursor: pointer;

  .edit-bill-date-btn {
    display: none;
  }

  &:hover {
    .edit-bill-date-btn {
      display: flex;
      justify-content: flex-end;
    }
  }
`;

const OtherDiscountStyles = styled.div`
  display: flex !important;
  align-items: center;
  width: 100%;
  cursor: pointer;

  .promotion-button {
    display: none;
  }

  &:hover {
    .promotion-button {
      display: flex;
      justify-content: flex-end;
    }
  }
`;

const NextBillDateDisabledCellStyles = styled.div({
  marginTop: token('space.100'),
  cursor: 'disabled',
  span: {
    color: token('color.text.disabled'),
  },
});

export const CartLineItemRowV2 = ({
  columns,
  product,
  linkedEntitlements,
  productIdPlanOptionsMap,
  selectedBillDate,
  setSelectedPricingPlanId,
  setActiveUsers,
  onRemoveProductFromCart,
  handleCheckedProductLineItems,
  selectiveProductRemovalEnable,
  checkedBillDate,
  shouldShowTrialStatus,
  matchBillDateEnabled,
  enrichmentStatus,
  isAllowedToEditUsersCount,
  isOrgProduct,
  showSandboxProducts,
  isSandboxDecouplingEnabled,
  isNonCommercialPricingEnabled,
  isExpandableRow,
  isRowHighlighedForReview,
  cloudSite,
  cloudSiteId,
  handleMatchBillDates,
  checkedItemMap,
  handleAddNewAppsClick,
  isDefaultExpanded,
  isTWCPhase2Enabled,
  isAppificationEnabled,
  setHasErrorFetchingOfferings,
  isMultiCurrencyEnabled,
  isAddOnsSearchEnabled,
}: CartLineItemProps) => {
  const [
    { products, selectedLinkedEntitlements, offeringsAndpricingPlans, selectedProducts, featureFlags },
  ] = useStore();
  const orgProductInstancesMap = useOrgProductInstancesMap();
  const productIdPriceEligibilityMap = usePricingPlanEligibility();
  const billingProfileDetails = useDestinationBillingProfileDetails();

  const isAddOnsPhase2Enabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_ADD_ONS_PHASE_V2] ?? false;
  const isAppEditionSupportEnabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_APP_EDITION_SUPPORT] ?? false;
  const hasLinkedAddOns = linkedEntitlements?.some(
    (linkedEntitlement) => linkedEntitlement?.relationshipType === LinkedEntitlementRelationShipType.ADD_ON
  );

  /**
   * In order to check if it is org instance,
   * we need to check if it has a parent entitlement id.
   * And then check if the parent entitlement id is present in the orgProductInstancesMap.
   * As keys of orgProductInstancesMap are org products.
   */
  const isOrgInstance = !!(
    product.parentEntitlementId && Object.keys(orgProductInstancesMap).includes(product.parentEntitlementId)
  );

  const isInTrial = ({
    lineItem,
    isLinkedEntitlement,
  }: {
    lineItem: cartLinkedEntitlement | Product;
    isLinkedEntitlement?: boolean;
  }) => {
    const currentOfferingId = products?.[lineItem.id]?.offeringId;
    const updatedOfferingId = lineItem.offeringId;

    // if initial offering id and updated offering id are same, then we can use the inTrial status from the product
    if (currentOfferingId === updatedOfferingId) return lineItem?.inTrial;

    // if the product is an app, then we can use the inTrial status from the app
    if (isLinkedEntitlement) return lineItem?.inTrial || OperationType.NEW === lineItem.operationType;

    // we are calculating the trial status based on initial offering id and updated offering id
    return getTrialStatusForEntitlement({
      currentOfferingId: currentOfferingId,
      updatedOfferingId: updatedOfferingId,
      productId: products?.[lineItem.id]?.productId,
      inCurrentlyInTrial: !!products?.[lineItem.id]?.inTrial,
      offeringsAndpricingPlans: offeringsAndpricingPlans || {},
    });
  };

  const renderInlineLoaderOrContent = ({
    content,
    shouldShowInlineLoader,
  }: {
    content: JSX.Element;
    shouldShowInlineLoader?: boolean;
  }) => {
    if (shouldShowInlineLoader) {
      return (
        <StyledLoaderWrapper data-testId="inline-loader">
          <SkeletonItem isShimmering />
        </StyledLoaderWrapper>
      );
    }

    return content;
  };

  const renderReadOnlyPlan = (
    offeringName?: string,
    isNonCommercialPricingEnabled?: boolean,
    isLinkedEntitlement?: boolean,
    priceEligibility?: `${PricingPlanType}`
  ) => {
    if (!offeringName) return <NoData />;
    if (!isNonCommercialPricingEnabled || !priceEligibility || !isLinkedEntitlement) {
      return <Text color="color.text">{offeringName}</Text>;
    }

    return (
      <div>
        <div>{offeringName}</div>
        <div className="product-rows-container--price-eligibility">{splitAndCapitalize(priceEligibility)}</div>
      </div>
    );
  };

  /**
   * isTransferStandardPaid is true if the product is a transfer and the offering is standard and the product is not in trial.
   * This is used to filter out the premium offering from the plan type dropdown in cart.
   *
   * Fetching products offering from store i.e products?.[lineItem.id].offeringId and not from lineItem.offeringId directly,
   * because say for isTransferStandardPaid lineitem we show offering standard and free, and we change plan type to free in cart not submitting the order,
   * in this case isTransferStandardPaid is false as offering for lineItem is changed to free.
   */
  const isTransferStandardPaid = (lineItem: cartLinkedEntitlement | Product, isLinkedEntitlement: boolean) => {
    return !isLinkedEntitlement
      ? lineItem.operationType === OperationType.TRANSFER &&
          productIdPlanOptionsMap[lineItem.id]?.find(
            (option) => option.offeringId === products?.[lineItem.id]?.offeringId
          )?.offeringName === 'Standard' &&
          lineItem.inTrial === false
      : false;
  };

  const columnMap = useCallback(
    ({ lineItem, columnName }: { lineItem: cartLinkedEntitlement | Product; columnName: string }) => {
      // @ts-ignore
      const isApp = lineItem?.relationshipType === LinkedEntitlementRelationShipType.APP;
      // @ts-ignore
      const isAddOn = lineItem?.relationshipType === LinkedEntitlementRelationShipType.ADD_ON;
      const isLinkedEntitlement = isApp || isAddOn || (!!isTWCPhase2Enabled && isOrgInstance);
      /**
       * we are converting productId = offeringId for statuspage addons and saving in store.
       * we are making this change because all statuspage addons have same product id
       * here is doc for more info https://hello.atlassian.net/wiki/spaces/~70121d05ec16fa6f7444db10a305ee7e0df3c/pages/5047030716/Statuspage+addons+support+in+UOF
       */
      const isStatuspageAddOn = isAddOn && isStatusPageAddOnProduct(lineItem?.productId);
      const isOfferingChangeAllowedForApp = isAppEditionSupportEnabled && isApp;
      const appProductStatuses = isLinkedEntitlement
        ? selectedLinkedEntitlements?.[lineItem.id]?.productStatuses ?? []
        : []; // For apps use the updated status from selected app from store
      const productStatuses = uniq([...(lineItem.productStatuses ?? []), ...appProductStatuses]);

      const productSearchableRelationshipTypes = getProductsSearchableRelationshipTypes(lineItem?.productId);
      const isAppSearchable = productSearchableRelationshipTypes.includes(ProductRelationShipType.APP);
      const isAddOnSearchable =
        productSearchableRelationshipTypes.includes(ProductRelationShipType.ADD_ON) &&
        (isAddOnsPhase2Enabled || isStatuspage(lineItem?.productId));

      const tooltipContent = (() => {
        if (isAppSearchable && isAddOnSearchable) {
          return `Add ${isAppificationEnabled ? 'Marketplace ' : ''}app/add-on`;
        }
        if (isAddOnSearchable) {
          return 'Add add-on';
        }
        return `Add ${isAppificationEnabled ? 'Marketplace ' : ''}app`;
      })();

      const isEnabledToEditUsers =
        ((isAllowedToEditUsersCount || lineItem.operationType === OperationType.NEW) &&
          !isApp &&
          !(isTWCPhase2Enabled && isOrgInstance)) ||
        (isAllowedToEditUsersCount && isAddOn);

      const hasLinkedSandboxesToEntitlement =
        isSandboxDecouplingEnabled && lineItem?.linkedSandboxIds ? lineItem?.linkedSandboxIds?.length > 0 : false;

      const hasSandboxesLinked =
        showSandboxProducts &&
        Boolean(
          Object.values(products)?.find(
            (productItem) =>
              selectedProducts?.[productItem?.id] &&
              productItem?.isSandbox &&
              lineItem?.linkedSandboxIds?.includes(productItem?.id)
          )
        );

      const hasAllData = lineItem.hasAllData;
      const isLoadingEnrichment = enrichmentStatus?.[lineItem.id] === EnrichmentStatus.PENDING;
      const shouldShowInlineLoader = !hasAllData && isLoadingEnrichment;
      const planTypeCellComponent = (
        <LineItemPlanCell
          setSelectedPricingPlanId={setSelectedPricingPlanId}
          productIdPlanOptionsMap={productIdPlanOptionsMap}
          lineItem={lineItem}
          isTransferStandardPaidProduct={isTransferStandardPaid(lineItem, isLinkedEntitlement)}
          selectedOrDefaultPriceEligibility={
            isNonCommercialPricingEnabled
              ? productIdPriceEligibilityMap[isStatuspageAddOn ? STATUSPAGE_PRODUCT_ID : lineItem.productId]
              : undefined
          }
          isOnUpgradeTrial={lineItem.isOnUpgradeTrial}
          hasLinkedAddOns={hasLinkedAddOns}
          renderReadOnlyPlan={renderReadOnlyPlan}
          isLinkedEntitlement={isLinkedEntitlement}
          setHasErrorFetchingOfferings={setHasErrorFetchingOfferings}
          isMultiCurrencyEnabled={isMultiCurrencyEnabled}
          isAddOnsSearchEnabled={isAddOnsSearchEnabled}
          hasSandboxesLinked={hasSandboxesLinked || hasLinkedSandboxesToEntitlement}
          orgProductInstancesMap={orgProductInstancesMap}
          isTWCPhase2Enabled={isTWCPhase2Enabled}
        />
      );

      const renderPlanTypeCellComponent = () => {
        if (isOfferingChangeAllowedForApp) {
          return planTypeCellComponent;
        }
        if (
          (isTWCPhase2Enabled && isOrgInstance) ||
          isLinkedEntitlement ||
          (isOrgProduct && lineItem.productId === ROVO_PRODUCT_ID) ||
          lineItem.isSandbox
        ) {
          return (
            renderReadOnlyPlan(
              lineItem?.offeringName,
              isNonCommercialPricingEnabled,
              isLinkedEntitlement,
              productIdPriceEligibilityMap[isStatuspageAddOn ? STATUSPAGE_PRODUCT_ID : lineItem.productId]
            ) ?? <NoData />
          );
        }
        return planTypeCellComponent;
      };

      switch (columnName) {
        case CART_COLUMNS_CONSTANTS.SELECT: {
          const selectCheckBoxNode = (() => {
            if (isLinkedEntitlement && lineItem.operationType !== OperationType.NEW) {
              if (selectiveProductRemovalEnable) {
                return <SpacingBeforeIntegratedApps />;
              }
              return null;
            }
            if (selectiveProductRemovalEnable) {
              return (
                <Checkbox
                  testId={`select-product-checkbox-${lineItem?.id}`}
                  isChecked={!!checkedItemMap?.[lineItem.id]}
                  onChange={() => handleCheckedProductLineItems(lineItem.id)}
                  label=""
                />
              );
            }
            return (
              <Button
                iconBefore={<CrossIcon size="small" label="" />}
                appearance="subtle"
                className="product-rows-container--delete-btn"
                onClick={() => onRemoveProductFromCart(lineItem.id)}
                testId={`remove-product-from-cart-btn-${lineItem?.id}`}
              />
            );
          })();
          return <td className="col-select-product">{selectCheckBoxNode}</td>;
        }
        case CART_COLUMNS_CONSTANTS.PRODUCT:
        case CART_COLUMNS_CONSTANTS.SUBSCRIPTION:
          return (
            <td
              className={classnames('col-product-name', {
                'included-product-cta': isTWCPhase2Enabled && Object.keys(orgProductInstancesMap).includes(lineItem.id),
              })}
            >
              <EntitlementItemCell
                name={lineItem?.name}
                logoId={lineItem?.logoId}
                // this is required for statuspage addons to show correct icon
                productId={isStatuspageAddOn ? STATUSPAGE_ADDON_PRODUCT_ID : lineItem?.productId}
                entitlementNumber={lineItem?.entitlementNumber}
                inTrial={shouldShowTrialStatus && isInTrial({ lineItem, isLinkedEntitlement })}
                inUpgradeTrial={shouldShowTrialStatus && lineItem.isOnUpgradeTrial}
                shouldShowSandboxStatus={
                  (showSandboxProducts || isSandboxDecouplingEnabled) &&
                  lineItem.isSandbox &&
                  lineItem.productId !== ROVO_PRODUCT_ID
                }
                shouldShowIncludedProducts={isTWCPhase2Enabled}
                orgInstances={orgProductInstancesMap?.[lineItem?.id]}
                isApp={(lineItem as cartLinkedEntitlement)?.relationshipType === LinkedEntitlementRelationShipType.APP}
              />
            </td>
          );
        case CART_COLUMNS_CONSTANTS.PLAN:
          return <td className={'col-common col-product-plan'}>{renderPlanTypeCellComponent()}</td>;
        case CART_COLUMNS_CONSTANTS.QUANTITY:
          return (
            <td className={'col-common col-product-quantity'}>
              {renderInlineLoaderOrContent({
                content: (
                  <>
                    {isEnabledToEditUsers ? (
                      <ActiveUsersCell
                        activeUsers={lineItem.activeUsers}
                        setActiveUsers={(updatedActiveUsers) => {
                          setActiveUsers(updatedActiveUsers, lineItem.id);
                        }}
                        isUserCountLimited1To99={
                          isStatusPageAddOnProduct(lineItem?.productId) || isStatuspage(lineItem?.productId)
                        }
                      />
                    ) : (
                      <Text color="color.text">
                        {isTWCPhase2Enabled && isOrgInstance && !isApp ? <NoData /> : lineItem.activeUsers}
                      </Text>
                    )}
                  </>
                ),
                shouldShowInlineLoader,
              })}
            </td>
          );
        case CART_COLUMNS_CONSTANTS.NEXT_BILL_DATE: {
          const billDateNode = (() => {
            if (isTWCPhase2Enabled && isOrgInstance && !isApp) {
              return <NoData />;
            }

            if (matchBillDateEnabled) {
              if (checkedBillDate && checkedBillDate[lineItem.id]) {
                return (
                  <>
                    <FormattedDate date={checkedBillDate[lineItem.id]} />
                  </>
                );
              }
              return lineItem.nextBillDate ? <FormattedDate date={lineItem.nextBillDate} /> : <NoData />;
            }

            if (selectedBillDate) {
              return (
                <>
                  <FormattedDate date={selectedBillDate} />
                </>
              );
            }
            return lineItem.nextBillDate ? <FormattedDate date={lineItem.nextBillDate} /> : <NoData />;
          })();
          return (
            <td className={'col-common col-product-next-bill-date'}>
              <NextBillDateCellStyles>
                {renderInlineLoaderOrContent({
                  content: !isLinkedEntitlement ? (
                    <>
                      <div className="product-rows-container--next-bill-date-column">{billDateNode}</div>
                      <div className="edit-bill-date-btn">
                        <Tooltip content="Change bill date">
                          <IconButton
                            label="edit-bill-date"
                            icon={EditIcon}
                            onClick={() => handleMatchBillDates?.(lineItem?.id)}
                            appearance="subtle"
                          />
                        </Tooltip>
                      </div>
                    </>
                  ) : (
                    <div className="product-rows-container--next-bill-date-column">{billDateNode}</div>
                  ),
                  shouldShowInlineLoader,
                })}
              </NextBillDateCellStyles>
            </td>
          );
        }
        case CART_COLUMNS_CONSTANTS.LIST_PRICE:
          return (
            <td className={'col-common'} data-testid="product-list-price">
              {!isNil(lineItem.listPrice) ? <FormatCurrency value={lineItem.listPrice} showCommas /> : <NoData />}
            </td>
          );
        case CART_COLUMNS_CONSTANTS.PARTNER_DISCOUNT:
          return (
            <td className={'col-common'} data-testid="product-partner-margin">
              {!isNil(lineItem.partnerMargin) ? (
                <FormatCurrency value={lineItem.partnerMargin} showCommas />
              ) : (
                <NoData />
              )}
            </td>
          );
        case CART_COLUMNS_CONSTANTS.OTHER_DISCOUNT: {
          const promotionsComponent = (
            <ViewProductPromotions
              isApp={isApp}
              productId={lineItem?.productId}
              id={lineItem?.id}
              discount={lineItem?.otherDiscount}
              isDesignIteration1Enabled={true}
            />
          );
          return (
            <td className={'col-common col-product-other-discount'}>
              {renderInlineLoaderOrContent({
                content: (
                  <OtherDiscountStyles>
                    <div className="product-rows-container--app-name-column" data-testid="app-other-discount">
                      {!isNil(lineItem.otherDiscount) ? (
                        <Flex alignItems="center" justifyContent="space-between" key={lineItem.id}>
                          <FormatCurrency value={lineItem.otherDiscount} showCommas />
                          {promotionsComponent}
                        </Flex>
                      ) : (
                        <>
                          <NoData />
                          {promotionsComponent}
                        </>
                      )}
                    </div>
                  </OtherDiscountStyles>
                ),
              })}
            </td>
          );
        }
        case CART_COLUMNS_CONSTANTS.STATUS:
          return (
            <td className={'col-common col-product-status'}>
              {productStatuses?.map((status) => {
                return (
                  <ProductStatusLozenge
                    className="product-status-column"
                    key={`product-status-${status}`}
                    status={status}
                  />
                );
              })}
            </td>
          );
        case CART_COLUMNS_CONSTANTS.TOTAL:
          return (
            <td className={'col-common'} data-testid="product-total">
              <div>{!isNil(lineItem.total) ? <FormatCurrency value={lineItem.total} showCommas /> : <NoData />}</div>
            </td>
          );
        case CART_COLUMNS_CONSTANTS.TAX_ESTIMATE:
          return (
            <td className={'col-common'} data-testid="product-tax-estimate">
              {!isNil(lineItem.totalTax) ? (
                <div>
                  <FormatCurrency value={lineItem.totalTax} showCommas />
                </div>
              ) : (
                <NoData />
              )}
            </td>
          );
        default:
          return (
            <td className={'col-product-add-new'}>
              {!isLinkedEntitlement && !isOrgProduct && (isAppSearchable || isAddOnSearchable) ? (
                <AddNewProductsWrapper
                  cloudSiteHost={cloudSite ?? ''}
                  isNetNewSite={!cloudSite}
                  addNewProductBtnAppearance={'default'}
                  cloudSiteId={cloudSiteId}
                  isDisabled={false}
                  hideAddNewButtonLabel={true}
                  handleAddNewAppsClick={handleAddNewAppsClick}
                  product={product}
                  tooltipContent={tooltipContent}
                />
              ) : null}
            </td>
          );
      }
    },
    [
      product,
      linkedEntitlements,
      productIdPlanOptionsMap,
      productIdPriceEligibilityMap,
      selectedBillDate,
      checkedItemMap,
      checkedBillDate,
      enrichmentStatus,
      isNonCommercialPricingEnabled,
      isOrgProduct,
      shouldShowTrialStatus,
      showSandboxProducts,
    ]
  );

  const appRow = useMemo(() => {
    return linkedEntitlements?.map((app) => {
      const row = columns.map((columnName) => {
        return columnMap({ lineItem: app, columnName });
      });
      return (
        <tr key={`app-line-item-row-${app.id}`} className="app-line-item-row">
          {row}
        </tr>
      );
    });
  }, [linkedEntitlements, columns]);

  const productRow = useMemo(() => {
    const rows = columns.map((columnName) => {
      return columnMap({ lineItem: product, columnName });
    });
    return rows;
  }, [product, columns]);

  if (!isExpandableRow) {
    return (
      <tr
        data-testid={`product-line-item-row-${product.id}`}
        className={classnames('no-expandable-row product-line-item-row', {
          'needs-review-row': isRowHighlighedForReview,
        })}
      >
        {productRow}
      </tr>
    );
  }

  return (
    <ExpandableRow
      rowsAsCells={[productRow]}
      className={`product-line-item-row ${isRowHighlighedForReview ? 'needs-review-row' : ''}`}
      subRows={appRow}
      expandableRowColumn={1}
      testId={`product-line-item-row-${product.id}`}
      isDefaultExpanded={isDefaultExpanded}
    />
  );
};
